if (!customElements.get('mr-header-autocomplete')) {
  customElements.define(
    'mr-header-autocomplete',
    class extends HTMLElement {
      constructor() {
        super();
      }

      popularCache = null;

      connectedCallback() {
        // 计算并设置动态高度
        this.updateAutocompleteHeight();
        
        // 监听窗口大小改变和页面滚动，重新计算高度
        window.addEventListener('resize', () => {
          this.updateAutocompleteHeight();
        });
        
        // 监听 announcement bar 关闭事件
        window.addEventListener('announcementBarClosed', () => {
          this.updateAutocompleteHeight();
        });
        
        // A11y (#103): wire up the ARIA combobox pattern. Per-instance ids
        // (two widgets exist -- desktop + mobile) come from a global counter so
        // aria-controls / aria-activedescendant never collide between them.
        this.psIndex = window.__predictiveSearchInstance =
          (window.__predictiveSearchInstance ?? -1) + 1;
        this.activeIndex = -1;
        this.options = [];
        this.searchSeq = this.searchSeq || 0;
        this.renderedSeq = -1; // round 4: no render belongs to any query yet
        this.showingResults = false; // round 6: popup holds fetched results (not popular)
        this.listboxId = `predictive-search-listbox-${this.psIndex}`;
        this.querySelector('.autocomplete-wrapper-inner').id = this.listboxId;
        this.querySelector('input').setAttribute('aria-controls', this.listboxId);
        this.statusRegion = this.querySelector('.hidden-label[role="status"]');

        // A11y (#103, reviews C3/R3/round 4): header.js owns close paths this
        // element never sees (search-toggle close, desktop menu mouseenter)
        // plus the open path -- all of them only mutate the field wrapper's
        // .active class (the wrapper is OUR child, header.js is shared and
        // stays untouched). Watch that class so aria-expanded can never
        // desync. All open-side decisions go through the ONE canonical
        // predicate (getCurrentResultsState); a popup without current
        // navigable results (popular list, no-results text, whitespace query,
        // mid-gap stale markup) is NEVER bare-expanded -- it stays collapsed,
        // matching the C7 posture, and absence is only ever announced from
        // the fetch-settle site.
        this.fieldWrapper = this.querySelector('[class*="__searchField-wrapper"]');
        if (this.fieldWrapper) {
          this.wrapperObserver = new MutationObserver(() => {
            if (!this.fieldWrapper.classList.contains('active')) {
              // Round 4: closing advances the query token (setExpanded(false)
              // -> invalidateQuery), obsoleting any in-flight chain (F2).
              // But toggle-close keeps the input AND the rendered DOM -- when
              // that render was CURRENT at close time, re-sync renderedSeq so
              // reopening onto the intact result set stays navigable (R3). A
              // mid-gap close (a newer query already advanced the token)
              // stays incoherent, so stale markup can never be re-exposed on
              // reopen (F1).
              const domWasCurrent = this.renderedSeq === this.searchSeq;
              this.invalidateQuery();
              this.setExpanded(false);
              if (domWasCurrent) this.renderedSeq = this.searchSeq;
              // Round 5 (Fable): the token advance above obsoletes any
              // in-flight chain -- including the fetch .then that would have
              // hidden the loading spinner. Every other dismissal hides it in
              // its own handler; this observer branch is the only one that
              // did not, leaving the spinner stuck after a toggle-close
              // mid-fetch. Mirror the other close paths.
              this.querySelector('.autocomplete-loading').style.display = 'none';
              // Round 5 (X2): when the on-screen markup does NOT belong to a
              // completed current render (mid-gap close), hide the container
              // too -- ARIA collapse alone would let a reopen visibly show
              // stale/partial markup (fetch1 paints display:block before
              // writing, and a suggestions-less response leaves the previous
              // query's markup standing). An intact current render stays
              // visible for the navigable reopen (R3).
              // Round 6: gated on showingResults -- only genuinely stale
              // FETCHED-results markup is ever hidden. The popular list never
              // sets the flag (and every popularCache restore clears it), so
              // it is structurally un-hideable here.
              if (!domWasCurrent && this.showingResults) {
                this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
              }
            } else {
              const state = this.getCurrentResultsState();
              if (state.navigable) {
                this.refreshOptions();
                this.setExpanded(true);
                this.announceCount();
              } else {
                // Round 6: pure collapse -- opening onto a non-navigable
                // surface (popular list, no-results render) is NOT a
                // query-context change; advancing the token here made the
                // legitimate initial popular list "incoherent" the moment
                // the widget opened.
                this.clearNavState();
                this.setExpanded(false);
              }
            }
          });
          this.wrapperObserver.observe(this.fieldWrapper, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        // Review R1 / round 4: crossing the 768px CSS breakpoint flips which
        // .result-item nodes are display:none while this.options is frozen.
        // Same canonical predicate as the observer. Refinement over a blanket
        // teardown: when not navigable, only tear down state that actually
        // exists (an exposed popup, or a stale collected list) -- an
        // unconditional invalidate would advance the query token and kill a
        // legitimately pending chain (mid-gap crossing; F1 requires that
        // chain to still settle and render coherently).
        if (!this.breakpointBound) {
          this.breakpointBound = true;
          this.breakpointQuery = window.matchMedia('(max-width: 768px)');
          this.onBreakpointChange = () => {
            const state = this.getCurrentResultsState();
            if (state.navigable) {
              this.refreshOptions();
              this.announceCount();
            } else {
              // Round 6: breakpoint teardown never changes the query
              // context -- pure nav-state clears only (an advance here
              // branded settled renders incoherent AND could kill a
              // legitimately pending chain, F1).
              const input = this.querySelector('input');
              if (input && input.getAttribute('aria-expanded') === 'true') {
                this.clearNavState();
                this.setExpanded(false);
              } else if (this.options.length) {
                this.clearNavState();
              }
              // Round 5 (X2) gated by round 6: only genuinely stale fetched
              // results are hidden (showingResults); popular markup is
              // structurally exempt. A current chain that later settles
              // re-exposes the container itself (settle-site counterpart).
              if (this.showingResults && this.renderedSeq !== this.searchSeq) {
                const inner = this.querySelector('.autocomplete-wrapper-inner');
                if (inner) inner.style.display = 'none';
              }
            }
          };
          this.breakpointQuery.addEventListener('change', this.onBreakpointChange);
        }

        // Review R4: the server-rendered popular-searches list has never been
        // through refreshOptions, so its anchors are native Tab stops.
        this.excludeFromTabOrder();

        this.querySelector('input').addEventListener('input', (e) => {
          const value = e.target.value;
          // A11y (#103): typing invalidates the keyboard-active option AND
          // the collected option list -- the old nodes are about to be
          // replaced/hidden, and arrows must be inert until the new render
          // lands (stale-options defect, review B1).
          this.invalidateQuery();

          // Round 6 minor: whitespace-only input routes to the empty branch
          // (popular restore, no spinner, NO fetch) -- trim is for the
          // EMPTINESS check only; the query sent for real input stays raw
          // ('gold ' still searches as typed).
          if (value.trim().length > 0) {
            this.querySelector('.clear-wrapper').style.display = 'block';
            this.querySelector('.autocomplete-loading').style.display = 'flex';
            this.debounceSearch(value);
          } else {
            // 当输入为空时，清理并隐藏 autocomplete
            // A11y (#103): empty input -> dropdown hidden -> collapsed
            // (query context already invalidated at the handler top).
            this.setExpanded(false);
            this.querySelector('.clear-wrapper').style.display = 'none';
            this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
            this.querySelector('.autocomplete-loading').style.display = 'none';
            document.querySelector('.shopify-section-header')?.classList.remove('searchOpened');
            // 恢复 popular searches
            if (this.popularCache !== null) {
              this.querySelector('.autocomplete-wrapper-inner').innerHTML = this.popularCache;
              this.excludeFromTabOrder(); // review R4
              this.showingResults = false; // round 6: popular restored
            }
          }
        });

        this.querySelector('input').addEventListener('keydown', (event) => {
          // A11y (#103): ArrowDown/ArrowUp move the active option (wrapping;
          // from none, Down -> first / Up -> last). DOM focus stays on the
          // input; only aria-activedescendant + the highlight class move.
          if (event.key === 'ArrowDown') {
            if (this.options.length) {
              event.preventDefault();
              this.moveActive(1);
            }
            return;
          }
          if (event.key === 'ArrowUp') {
            if (this.options.length) {
              event.preventDefault();
              this.moveActive(-1);
            }
            return;
          }
          if (event.key === 'Enter') {
            // A11y (#103): Enter on an active option navigates to ITS url and
            // suppresses submit. Enter with no active option keeps the original
            // behaviour byte-identical (go to /search?q=<value>). Review C6:
            // modified Enter (cmd/ctrl/shift/alt) skips the active-option
            // branch entirely so it is never forced into a same-tab
            // navigation it did not have before.
            const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
            const activeOption = modified ? undefined : this.options[this.activeIndex];
            const activeUrl = this.getOptionUrl(activeOption);
            if (!modified && this.activeIndex >= 0 && activeUrl) {
              event.preventDefault();
              event.stopPropagation();
              location.href = activeUrl;
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            if (this.querySelector('input').value) {
              location.href = `/search?q=${encodeURIComponent(this.querySelector('input').value)}&type=product`;
            }
          }
        });

        this.querySelectorAll('.popular-searches .suggestion').forEach((suggestion) => {
          suggestion.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (suggestion.textContent.length > 0) {
              this.querySelector('input').value = suggestion.textContent;
              this.querySelector('.clear-wrapper').style.display = 'block';
              document.querySelector('.autocomplete-overlay').style.display = 'block';
              this.debounceSearch(suggestion.textContent);
            }
          });
        });

        this.querySelector('.clear-wrapper').addEventListener('click', () => {
          // A11y (#103): clearing is a query-context change.
          this.invalidateQuery();
          this.setExpanded(false);
          this.querySelector('input').value = '';
          this.querySelector('.clear-wrapper').style.display = 'none';
          this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
          this.querySelector('.autocomplete-loading').style.display = 'none';
          // #206: a line here tried to remove 'searchOpened' from the header but
          // was written against a nonexistent class selector ('.shopify-section-header')
          // and threw on every CLEAR click, aborting the restore/refocus below. It
          // never once executed, and the search field stays open after CLEAR, so the
          // header's open-state class must remain for the toggle's close semantics --
          // the line is removed rather than repaired.
          // 恢复 popular searches
          if (this.popularCache !== null) {
            this.querySelector('.autocomplete-wrapper-inner').innerHTML = this.popularCache;
            this.excludeFromTabOrder(); // review R4
            this.showingResults = false; // round 6: popular restored
          }

          this.querySelector('input').focus();
        });

        this.querySelector('.search-icon').addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.querySelector('input').value) {
            location.href = `/search?q=${encodeURIComponent(this.querySelector('input').value)}&type=product`;
          }
        });

        // A11y (#99): by click time the browser's default mousedown behaviour
        // has already blurred whatever was focused (activeElement is <body>),
        // so record whether focus sat inside this widget at mousedown.
        document.querySelector('.autocomplete-overlay').addEventListener('mousedown', () => {
          this.overlayPointerDownHadFocusInside = this.contains(document.activeElement);
        });

        document.querySelector('.autocomplete-overlay').addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const focusWasInside =
            this.overlayPointerDownHadFocusInside || this.contains(document.activeElement);
          this.overlayPointerDownHadFocusInside = false;

          // A11y (#103): backdrop dismissal is a query-context change.
          this.invalidateQuery();
          this.setExpanded(false);
          this.querySelector('input').value = '';
          this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
          document.querySelector('.autocomplete-overlay').style.display = 'none';
          this.querySelector('.clear-wrapper').style.display = 'none';
          document.querySelector('.searchOpened')?.classList.remove('searchOpened');
          this.querySelector('.autocomplete-loading').style.display = 'none';
          if (window.innerWidth > 1024) {
            document.querySelector('.mrHeader__searchField').classList.remove('active');
            document.querySelector('.mrHeader__searchField-wrapper').classList.remove('active');
          } else {
            document.querySelector('.mrMobileHeader__searchField').classList.remove('active');
            document.querySelector('.mrMobileHeader__searchField-wrapper').classList.remove('active');
            document.querySelector('.mrMobileHeader__actionItem--icon').style.display = 'block';
            document.querySelector('.mrMobileHeader__actionItem--icon-close').style.display = 'none';

          }

          document.querySelector('html').classList.remove('no-scroll');
          this.querySelector('.autocomplete-wrapper-inner').style.display = 'block';
          if (this.popularCache !== null) {
            this.querySelector('.autocomplete-wrapper-inner').innerHTML = this.popularCache;
            this.excludeFromTabOrder(); // review R4
            this.showingResults = false; // round 6: popular restored
          }
          // A11y (#99): dismissing via the backdrop while typing must not
          // strand focus in the hidden field; when focus was elsewhere the
          // guard keeps this close path free of any focus side-effect.
          if (focusWasInside) {
            this.focusSearchToggle();
          }
        });

        this.querySelector('.close-icon').addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          // A11y (#99): captured before the close work below hides the field
          // (visibility gating) -- synchronously activeElement is still the
          // control that triggered this (close button on Enter/Space or
          // mouse-click, input/suggestion via the Escape path).
          const focusWasInside = this.contains(document.activeElement);
          // A11y (#103): canonical full close (also the Escape target) is a
          // query-context change.
          this.invalidateQuery();
          this.setExpanded(false);
          if (window.innerWidth > 1024) {
            this.querySelector('.mrHeader__searchField-wrapper').classList.remove('active');
          } else {
            document.querySelector('.mrMobileHeader__actionItem--icon').style.display = 'block';
            document.querySelector('.mrMobileHeader__actionItem--icon-close').style.display = 'none';

            this.querySelector('.mrMobileHeader__searchField-wrapper').classList.remove('active');
          }

          document.querySelector('.searchOpened')?.classList.remove('searchOpened');
          this.querySelector('.autocomplete-wrapper-inner').style.display = 'block';
          this.classList.remove('active');
          this.querySelector('input').value = '';
          this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
          document.querySelector('html').classList.remove('no-scroll');
          document.querySelector('.autocomplete-overlay').style.display = 'none';
          this.querySelector('.clear-wrapper').style.display = 'none';
          this.querySelector('.autocomplete-loading').style.display = 'none';
          if (this.popularCache !== null) {
            this.querySelector('.autocomplete-wrapper-inner').innerHTML = this.popularCache;
            this.excludeFromTabOrder(); // review R4
            this.showingResults = false; // round 6: popular restored
          }
          // A11y (#99): without this, activating Close strands focus inside
          // the now-hidden field (keyboard Enter/Space, and mouse too --
          // Chromium focuses a real <button> on mousedown). Guarded so
          // programmatic/unrelated invocations never steal focus from
          // elsewhere on the page.
          if (focusWasInside) {
            this.focusSearchToggle();
          }
        });

        // A11y (#99): Escape fully closes the search overlay from anywhere
        // inside the widget (input, CLEAR/close/search buttons, suggestion
        // links all bubble to this custom element). Reuses the existing
        // close-icon click path verbatim -- the canonical full close that
        // hides the dropdown + the page-blocking overlay and removes
        // html.no-scroll -- so keyboard dismissal can never leave the overlay
        // or scroll-lock stale (the empty-input aggravator), then returns
        // focus to the header search toggle that opened it. Synthesizing the
        // button's click (not the SVG's) avoids header.js's form-submit svg
        // handler, so Escape closes without navigating. Additive: never fires
        // for mouse users.
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            // Focus is necessarily inside the widget (this keydown bubbled
            // from it), so the close handler's focusWasInside guard passes
            // and restores focus to the header search toggle.
            this.querySelector('.close-icon').click();
          }
        });
      }

      // A11y (#99): shared focus-restoration target -- the header search
      // toggle that opened the overlay (header.js searchIconClickHandler).
      focusSearchToggle() {
        const toggle =
          window.innerWidth > 1024
            ? document.querySelector('.mrHeader__actionItem.action_search button')
            : document.querySelector('.mrMobileHeader__actionItem.action_search button');
        if (toggle) {
          toggle.focus({ preventScroll: true });
        }
      }

      // A11y (#103): re-collect options after every innerHTML swap and stamp
      // per-instance ids (the fetched markup is shared, so ids MUST be assigned
      // here, not server-side). role="option" is server-rendered in the
      // snippets and survives the swap. DOM order == visual order: query
      // suggestions, popular searches, product results, then View All.
      refreshOptions() {
        const listbox = this.querySelector('.autocomplete-wrapper-inner');
        // Review B2: only VISIBLE options are navigable/announced -- global.scss
        // hides .result-item:nth-child(n+5) on desktop (>768px un-hidden on
        // mobile), and keyboard users must never be cycled onto, or navigated
        // to, an option sighted users cannot see. offsetParent is null for
        // display:none nodes; the listbox itself is visible here (this runs
        // post-render, after style.display='block'), so the test is reliable.
        this.options = Array.from(
          listbox.querySelectorAll('.suggestion, .result-item, .results-footer')
        ).filter((opt) => opt.offsetParent !== null);
        this.options.forEach((opt, i) => {
          opt.id = `${this.listboxId}-option-${i}`;
          if (!opt.getAttribute('role')) opt.setAttribute('role', 'option');
          opt.setAttribute('aria-selected', 'false');
          opt.classList.remove('autocomplete-option--active');
          // Review C5 (APG): popup descendants are reached via
          // aria-activedescendant, not Tab -- remove the option anchors (and
          // any focusable content inside a product card) from the page Tab
          // sequence. Deliberate behaviour delta vs base: base users could
          // Tab into suggestion links; the combobox replaces that with
          // ArrowUp/ArrowDown, and Tab from the input goes to CLEAR/Close as
          // before. Mouse clicks are unaffected by tabindex.
          if (opt.tagName === 'A') opt.setAttribute('tabindex', '-1');
          opt.querySelectorAll('a, button').forEach((n) => n.setAttribute('tabindex', '-1'));
        });
        this.activeIndex = -1;
        this.querySelector('input').removeAttribute('aria-activedescendant');
      }

      // A11y (#103): resolve an option's navigation target. Query / popular /
      // View-All options are anchors themselves; a product .result-item row
      // carries its url on the inner <a> (the real link added in #94).
      getOptionUrl(opt) {
        if (!opt || !opt.isConnected) return null;
        if (opt.tagName === 'A' && opt.getAttribute('href')) return opt.href;
        const link = opt.querySelector('a[href]');
        return link ? link.href : null;
      }

      // A11y (#103): clear any keyboard-active option -- highlight, selection
      // state and aria-activedescendant. Called on typing, close and collapse.
      clearActive() {
        if (this.options && this.options[this.activeIndex]) {
          this.options[this.activeIndex].classList.remove('autocomplete-option--active');
          this.options[this.activeIndex].setAttribute('aria-selected', 'false');
        }
        this.activeIndex = -1;
        const input = this.querySelector('input');
        if (input) input.removeAttribute('aria-activedescendant');
      }

      // A11y (#103): make option `index` the active descendant -- paints the
      // highlight (see .autocomplete-option--active in global.scss) while DOM
      // focus stays on the input per the APG combobox pattern.
      setActive(index) {
        if (!this.options || !this.options.length) return;
        const opt = this.options[index];
        // Review B1 belt-and-braces: never activate a node detached by an
        // innerHTML swap (aria-activedescendant would dangle).
        if (!opt || !opt.isConnected) return;
        this.clearActive();
        this.activeIndex = index;
        opt.classList.add('autocomplete-option--active');
        opt.setAttribute('aria-selected', 'true');
        this.querySelector('input').setAttribute('aria-activedescendant', opt.id);
        opt.scrollIntoView({ block: 'nearest' });
      }

      // A11y (#103): ArrowDown/ArrowUp move + wrap; from no active option
      // ArrowDown -> first, ArrowUp -> last (APG).
      moveActive(dir) {
        if (!this.options || !this.options.length) return;
        const len = this.options.length;
        const next =
          this.activeIndex === -1
            ? dir > 0
              ? 0
              : len - 1
            : (this.activeIndex + dir + len) % len;
        this.setActive(next);
      }

      // A11y (#103): keep the input's aria-expanded in sync with dropdown
      // visibility. Round 6: PURE attribute setter -- collapse side effects
      // are explicit at each call site (invalidateQuery for query-context
      // changes, clearNavState for pure collapses), so a collapse can never
      // accidentally brand a legitimate surface as incoherent.
      setExpanded(expanded) {
        const input = this.querySelector('input');
        if (input) input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      // Round 6 split -- two primitives with distinct semantics:
      //
      // clearNavState(): drop ONLY the keyboard-navigation surface (options,
      // active option, activedescendant, status text). Used by pure ARIA
      // collapses that leave the query context intact (C7 settle-collapse,
      // the observer's non-navigable open branch, breakpoint teardown).
      // Advancing the token there wrongly branded legitimate surfaces
      // (initial popular list, a settled no-results render) as incoherent.
      // NOT merged into clearActive() (review B1): setActive() calls
      // clearActive() before indexing this.options.
      clearNavState() {
        this.announce('');
        this.clearActive();
        this.options = [];
      }

      // invalidateQuery(): a REAL query-context change -- typing, empty
      // input, CLEAR, backdrop, close-icon, toggle-close, debounceSearch
      // entry/timeout. clearNavState PLUS: advances the token so every
      // in-flight fetch chain obsoletes itself (round 4/F2 -- a late
      // response can never overwrite a dismissed or restored popup), and
      // cancels the PENDING debounce timer (round 5/X1 -- a dismissal inside
      // the 500ms window must not let the callback later resurrect itself as
      // current and fetch into a closed widget). Retype stays safe: the
      // input handler invalidates (clearing the old timer) BEFORE
      // debounceSearch sets the new one; the timeout callback invalidates
      // before capturing its token, so clearing its own already-fired handle
      // is a harmless no-op.
      invalidateQuery() {
        this.searchSeq = (this.searchSeq || 0) + 1;
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
          this.debounceTimeout = null;
        }
        this.clearNavState();
      }

      // A11y (#103): announce into the persistent role="status" region. Called
      // once per settled fetch (debounced) to avoid keystroke chatter.
      announce(message) {
        if (this.statusRegion) this.statusRegion.textContent = message;
      }

      // Round 4: THE single predicate for "the popup currently shows a
      // navigable result set" -- the one decision site for every
      // expand/collect consumer (observer open branch, breakpoint listener).
      // Requires ALL of: field open, popup visible, a real (non-whitespace)
      // query, token coherence (what is on screen belongs to the CURRENT
      // query: renderedSeq is stamped at render completion, searchSeq
      // advances on every query-context change, so mid-gap stale markup
      // fails here -- F1) and at least one visible option.
      getCurrentResultsState() {
        const inner = this.querySelector('.autocomplete-wrapper-inner');
        const input = this.querySelector('input');
        const open = !!(this.fieldWrapper && this.fieldWrapper.classList.contains('active'));
        const visible = !!(inner && inner.style.display !== 'none');
        const hasQuery = !!(input && input.value.trim().length > 0);
        const coherent = this.renderedSeq === this.searchSeq;
        const options =
          visible && inner
            ? Array.from(
                inner.querySelectorAll('.suggestion, .result-item, .results-footer')
              ).filter((o) => o.offsetParent !== null)
            : [];
        return {
          navigable: open && visible && hasQuery && coherent && options.length > 0,
          options,
        };
      }

      // Round 4 (minor): close the listener lifecycle for the two bindings
      // this element owns beyond its subtree events -- a genuine
      // disconnect/reconnect (section re-render, DOM move) must not leave a
      // dead MutationObserver or stack a duplicate matchMedia listener.
      // Guards are reset so a reconnect rebinds cleanly exactly once.
      disconnectedCallback() {
        if (this.wrapperObserver) {
          this.wrapperObserver.disconnect();
          this.wrapperObserver = null;
        }
        if (this.breakpointQuery && this.onBreakpointChange) {
          this.breakpointQuery.removeEventListener('change', this.onBreakpointChange);
        }
        this.breakpointQuery = null;
        this.onBreakpointChange = null;
        this.breakpointBound = false;
      }

      // Reviews R1/R3: announce the current collected-option count. Only
      // called on a real re-collect (breakpoint crossing with a live popup,
      // toggle-reopen onto rendered results) -- never on pure resizes.
      announceCount() {
        const n = this.options.length;
        this.announce(n > 0 ? `${n} suggestion${n === 1 ? '' : 's'} available` : 'No suggestions');
      }

      // Review R4: popup content that never went through refreshOptions (the
      // server-rendered popular list at load, and every popularCache restore)
      // must still be out of the page Tab sequence (APG). tabindex only -- no
      // ids: popular options stay un-navigable (tracked separately).
      excludeFromTabOrder() {
        const inner = this.querySelector('.autocomplete-wrapper-inner');
        if (!inner) return;
        inner
          .querySelectorAll('.suggestion, .result-item, .results-footer')
          .forEach((opt) => {
            if (opt.tagName === 'A') opt.setAttribute('tabindex', '-1');
            opt.querySelectorAll('a, button').forEach((n) => n.setAttribute('tabindex', '-1'));
          });
      }

      updateAutocompleteHeight() {
        // 获取 header 高度
        const header = document.querySelector('#shopify-section-header');
        const headerHeight = header ? header.offsetHeight : 50; // 默认 50px
        
        // 获取 announcement bar 高度
        const announcementBar = document.querySelector('#shopify-section-announcement-bar');
        const announcementHeight = announcementBar && announcementBar.offsetHeight > 0 ? announcementBar.offsetHeight : 0;
        
        // 设置 CSS 变量
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
      }

      debounceTimeout;
      debounceSearch(value) {
        this.querySelector('.autocomplete-loading').style.display = 'flex';
        this.querySelector('.autocomplete-wrapper-inner').style.display = 'none';
        // A11y (#103): a new query -- the dropdown is hidden for the whole
        // loading gap, the combobox is collapsed and the old context is
        // obsoleted (invalidateQuery also makes debounceSearch
        // self-protecting when called directly, e.g. the popular-suggestion
        // click path). Each chain captures its token at timeout start --
        // AFTER the timeout's own invalidate -- and bails before ANY DOM
        // write once it is no longer current (review R2; also cures the
        // pre-existing visible stale-overwrite race for sighted users).
        this.invalidateQuery();
        this.setExpanded(false);
        clearTimeout(this.debounceTimeout);

        this.debounceTimeout = setTimeout(() => {
          // A11y (#103, review B1): belt-and-braces -- the fetch is about to
          // replace the markup; no stale options may survive into this window.
          // Round 4/6: invalidateQuery() advances searchSeq, so this chain's
          // token MUST be captured AFTER it (this chain IS the newest
          // context; capturing before would self-stale every fetch).
          this.invalidateQuery();
          const seq = this.searchSeq; // review R2: this chain's token
          // A11y (#103): announce the settled search start (debounced -> no
          // per-keystroke chatter); the count is announced once the swap lands.
          this.announce('Searching…');
          fetch(`/search/suggest?q=${encodeURIComponent(value)}&section_id=predictive-search`)
            .then((response) => response.text())
            .then((text) => {
              if (seq !== this.searchSeq) return; // review R2: stale response
              const parser = new DOMParser();
              const html = parser.parseFromString(text, 'text/html');
              this.querySelector('.autocomplete-wrapper-inner').style.display = 'block';

              if (this.popularCache === null) {
                // Review C4: capture OUR OWN pre-swap content. The previous
                // document.querySelector always grabbed the first (desktop)
                // widget's tree, so the mobile instance could cache -- and
                // later restore -- desktop's stamped option ids (duplicate
                // ids + stale aria-selected in the document).
                this.popularCache = this.querySelector('.autocomplete-wrapper-inner').innerHTML;
              }

              if (html.querySelector('.autocomplete-suggestions')) {
                this.querySelector('.autocomplete-wrapper-inner').innerHTML =
                  html.querySelector('.shopify-section').innerHTML;
                // Round 7: the container NOW shows fetched query content --
                // stamp the inserted anchors out of the Tab order immediately
                // (refreshOptions only runs at fetch2 settle; a toggle-close
                // in the fetch1->fetch2 gap previously left these natively
                // Tab-navigable under aria-expanded=false, repeatable after
                // any popular restore) and set showingResults here so the
                // incoherent-hide fires for mid-gap closes. Inside the seq
                // guard, so a stale chain can never set the flag; inside the
                // suggestion-write branch, so a suggestions-less response
                // (container still showing popular/previous content) never
                // marks that content as fetched results.
                this.excludeFromTabOrder();
                this.showingResults = true;
              }

              fetch(`/search?q=${encodeURIComponent(value)}&type=product&section_id=autocomplete-search`)
                .then((response) => response.text())
                .then((text) => {
                  if (seq !== this.searchSeq) return; // review R2: stale response
                  const parser = new DOMParser();
                  const searchHtml = parser.parseFromString(text, 'text/html');
                  if (html.querySelector('.autocomplete-suggestions')) {
                    if (searchHtml.querySelector('autocomplete-results')) {
                      this.querySelector('.autocomplete-container').appendChild(searchHtml.querySelector('autocomplete-results'));
                    } else {
                      this.querySelector('.autocomplete-container').appendChild(searchHtml.querySelector('.autocomplete-no-results'));
                    }
                  } else {
                    this.querySelector('.autocomplete-wrapper-inner').innerHTML = searchHtml.querySelector('.shopify-section').innerHTML;
                  }
                  this.querySelector('.autocomplete-loading').style.display = 'none';
                  // Round 4 (F1): the render for THIS token is now complete
                  // and on screen -- record it regardless of open state, so
                  // the canonical predicate can tell current markup from a
                  // previous query's leftovers.
                  this.renderedSeq = seq;
                  this.showingResults = true; // round 6: fetched results now on screen
                  // Review C3c: the user may have closed (toggle/Escape/CLEAR/
                  // backdrop -- wrapper loses .active and/or input emptied)
                  // while this fetch was in flight. A late-landing render must
                  // not re-expose the combobox, collect stale options, or
                  // announce into a dismissed widget. The visual repaint above
                  // is pre-existing behaviour and stays untouched.
                  const stillOpen =
                    !!(this.fieldWrapper && this.fieldWrapper.classList.contains('active')) &&
                    this.querySelector('input').value.trim().length > 0;
                  if (!stillOpen) {
                    this.announce('');
                    return;
                  }
                  // Round 5 (X2 counterpart): a mid-gap teardown may have
                  // hidden the container; this chain is current and the
                  // widget is open, so re-expose it before collecting
                  // (options are offsetParent-filtered and would all vanish
                  // inside a hidden container).
                  this.querySelector('.autocomplete-wrapper-inner').style.display = 'block';
                  // A11y (#103): dropdown markup has fully landed -- collect the
                  // fresh options (stamp ids), expose expanded, announce count.
                  this.refreshOptions();
                  const optionCount = this.options.length;
                  if (optionCount > 0) {
                    this.setExpanded(true);
                    this.announce(
                      `${optionCount} suggestion${optionCount === 1 ? '' : 's'} available`
                    );
                  } else {
                    // Review C7: a listbox exposed with zero owned options
                    // violates ARIA -- keep the combobox collapsed (the visible
                    // no-results text still shows) and announce the outcome.
                    // Round 6: pure collapse -- this settled render IS the
                    // current query's legitimate surface; advancing the token
                    // here wrongly branded it incoherent and let the next
                    // teardown hide it.
                    this.clearNavState();
                    this.setExpanded(false);
                    this.announce('No suggestions');
                  }
                })
                .catch((err) => {
                  // A11y (#103): a failed fetch must not leave the status
                  // region saying "Searching…" forever. Reset the text only;
                  // the pre-existing error posture is otherwise unchanged
                  // (re-logged for visibility). Review R2: a STALE chain's
                  // failure must not clear the CURRENT query's status.
                  if (seq === this.searchSeq) this.announce('');
                  console.error(err);
                });
            })
            .catch((err) => {
              if (seq === this.searchSeq) this.announce('');
              console.error(err);
            });
        }, 500); // 400ms debounce delay
      }
    }
  );
}

if (!customElements.get('autocomplete-results')) {
  customElements.define(
    'autocomplete-results',
    class extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
      }
      
      initLayout() {
      }
      
      handleResize() {
      }
    }
  );
}